import type { IExtractedFieldResponse } from "@/api/documents/documents.types";
import { Box, Typography } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useCallback, useRef, useState } from "react";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps,
} from "react-virtualized";
import "react-virtualized/styles.css";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
} from "../StyledAccordion";
import { DynamicArrayItem } from "./DynamicArrayItem";

interface IProps {
    field: IExtractedFieldResponse;
    pageNavigationPluginInstance: PageNavigationPlugin;
    defaultExpanded?: boolean;
    isEditable?: boolean;
    onSave?: (propertyId: number, value: string | null) => void;
    onDelete?: (propertyId: number) => void;
    onDeleteItem?: (
        fieldDefinitionId: number,
        arrayIndex: number,
        expectedDeleteFingerprint: string
    ) => void;
}

const MIN_ITEMS_FOR_VIRTUALIZATION = 10;
const MAX_LIST_HEIGHT = 600;
const DEFAULT_ROW_HEIGHT = 60;

export const DynamicArrayField = ({
    field,
    pageNavigationPluginInstance,
    defaultExpanded = false,
    isEditable = false,
    onSave,
    onDelete,
    onDeleteItem,
}: IProps) => {
    const listRef = useRef<List | null>(null);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const cache = useRef(
        new CellMeasurerCache({
            defaultHeight: DEFAULT_ROW_HEIGHT,
            minHeight: DEFAULT_ROW_HEIGHT,
            fixedWidth: true,
        })
    ).current;

    const handleFieldAccordionChange = useCallback(
        (_event: React.SyntheticEvent, expanded: boolean) => {
            setIsExpanded(expanded);
        },
        []
    );

    const handleItemAccordionChange = useCallback(
        (arrayIndex: number, rowIndex: number) =>
            (_event: React.SyntheticEvent, expanded: boolean) => {
                setExpandedItems((prev) => {
                    const newSet = new Set(prev);
                    if (expanded) {
                        newSet.add(arrayIndex);
                    } else {
                        newSet.delete(arrayIndex);
                    }
                    return newSet;
                });

                if (listRef.current) {
                    cache.clear(rowIndex, 0);
                    requestAnimationFrame(() => {
                        listRef.current?.recomputeRowHeights(rowIndex);
                    });
                }
            },
        [cache]
    );

    const rowRenderer = useCallback(
        ({ index, key, parent, style }: ListRowProps) => {
            const item = field.items[index];
            const isItemExpanded = expandedItems.has(item.array_index);

            return (
                <CellMeasurer
                    cache={cache}
                    columnIndex={0}
                    key={key}
                    parent={parent}
                    rowIndex={index}
                >
                    {({ registerChild, measure }) => {
                        const handleChange = (
                            event: React.SyntheticEvent,
                            expanded: boolean
                        ) => {
                            handleItemAccordionChange(item.array_index, index)(
                                event,
                                expanded
                            );
                            requestAnimationFrame(() => {
                                measure();
                            });
                        };

                        return (
                            <div
                                ref={registerChild as React.Ref<HTMLDivElement>}
                                style={style}
                            >
                                <DynamicArrayItem
                                    item={item}
                                    pageNavigationPluginInstance={
                                        pageNavigationPluginInstance
                                    }
                                    expanded={isItemExpanded}
                                    onChange={handleChange}
                                    isEditable={isEditable}
                                    onSave={onSave}
                                    onDelete={onDelete}
                                    onDeleteItem={
                                        onDeleteItem
                                            ? (expectedDeleteFingerprint) =>
                                                  onDeleteItem(
                                                      field.field_definition_id,
                                                      item.array_index,
                                                      expectedDeleteFingerprint
                                                  )
                                            : undefined
                                    }
                                />
                            </div>
                        );
                    }}
                </CellMeasurer>
            );
        },
        [
            field.items,
            field.field_definition_id,
            cache,
            expandedItems,
            pageNavigationPluginInstance,
            handleItemAccordionChange,
            isEditable,
            onSave,
            onDelete,
            onDeleteItem,
        ]
    );

    const getListHeight = useCallback((itemCount: number) => {
        const estimatedHeight = itemCount * DEFAULT_ROW_HEIGHT;
        return Math.min(estimatedHeight, MAX_LIST_HEIGHT);
    }, []);

    const showVirtualization =
        field.items.length >= MIN_ITEMS_FOR_VIRTUALIZATION;
    const listHeight = getListHeight(field.items.length);

    return (
        <Accordion expanded={isExpanded} onChange={handleFieldAccordionChange}>
            <AccordionSummary>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="body2">{field.field_name}</Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 2 }}
                    >
                        {field.items.length} item
                        {field.items.length !== 1 ? "s" : ""}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {showVirtualization ? (
                    <Box
                        sx={{
                            height: listHeight,
                            width: "100%",
                            "& .ReactVirtualized__List": {
                                "&::-webkit-scrollbar": {
                                    width: "8px",
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "transparent",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "rgba(0,0,0,0.2)",
                                    borderRadius: "4px",
                                },
                                "&::-webkit-scrollbar-thumb:hover": {
                                    backgroundColor: "rgba(0,0,0,0.3)",
                                },
                            },
                        }}
                    >
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <List
                                    ref={listRef}
                                    height={listHeight}
                                    width={width}
                                    rowCount={field.items.length}
                                    rowHeight={cache.rowHeight}
                                    rowRenderer={rowRenderer}
                                    overscanRowCount={2}
                                    deferredMeasurementCache={cache}
                                />
                            )}
                        </AutoSizer>
                    </Box>
                ) : (
                    <Box>
                        {field.items.map((item, index) => {
                            const isItemExpanded = expandedItems.has(
                                item.array_index
                            );
                            return (
                                <DynamicArrayItem
                                    key={item.array_index}
                                    item={item}
                                    pageNavigationPluginInstance={
                                        pageNavigationPluginInstance
                                    }
                                    expanded={isItemExpanded}
                                    onChange={handleItemAccordionChange(
                                        item.array_index,
                                        index
                                    )}
                                    isEditable={isEditable}
                                    onSave={onSave}
                                    onDelete={onDelete}
                                    onDeleteItem={
                                        onDeleteItem
                                            ? (expectedDeleteFingerprint) =>
                                                  onDeleteItem(
                                                      field.field_definition_id,
                                                      item.array_index,
                                                      expectedDeleteFingerprint
                                                  )
                                            : undefined
                                    }
                                />
                            );
                        })}
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
